import { useCallback } from 'react';

import type { CreateDashboardResult } from '@/features/dashboard/pages/Edit/components/menus';
import useSnackbarNotifications from '@/shared/hooks/useSnackbarNotifications';

export type MoveMenuCallbacks = {
  /**
   * Wraps createDashboardDraft to match DashboardTargetMenu's expected signature.
   */
  handleCreateDashboard: () => Promise<CreateDashboardResult | null>;
  /**
   * Shows error snackbar with the given message.
   */
  handleError: (message: string) => void;
  /**
   * Shows success snackbar with the given message.
   */
  handleSuccess: (message: string) => void;
  /**
   * Shows error snackbar - direct reference for external use.
   */
  showErrorSnackbar: (message: string) => void;
};

type UseMoveMenuCallbacksParams = {
  createDashboardDraft: () => unknown | Promise<unknown>;
};

/**
 * Hook providing common callbacks for move-to-dashboard menus.
 *
 * Extracts shared logic used by:
 * - MoveToDashboardMenu (widgets)
 * - MoveAppToDashboardMenu (apps)
 * - MoveTileToDashboardMenu (tiles)
 * - MoveTabToDashboardMenu (tabs)
 *
 * @example
 * ```tsx
 * const actions = useDashboardEditActions();
 * const { handleCreateDashboard, handleError } = useMoveMenuCallbacks({
 *   createDashboardDraft: actions.createDashboardDraft
 * });
 * ```
 */
export const useMoveMenuCallbacks = ({
  createDashboardDraft
}: UseMoveMenuCallbacksParams): MoveMenuCallbacks => {
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbarNotifications();

  /**
   * Handle creating a new dashboard draft.
   * Wraps context method to match DashboardTargetMenu's expected callback signature.
   */
  const handleCreateDashboard = useCallback(async (): Promise<CreateDashboardResult | null> => {
    const result = await createDashboardDraft();
    // Redux action returns { type, payload }
    if (result && typeof result === 'object' && 'payload' in result) {
      const payload = (result as { payload?: { dashboardName?: string; name?: string } }).payload;
      const dashboardName = payload?.dashboardName || payload?.name;
      if (dashboardName) {
        const successResult: CreateDashboardResult = { ok: true, dashboardName };
        return successResult;
      }
      const missingNameResult: CreateDashboardResult = {
        ok: false,
        message: 'Failed to create dashboard: missing name.'
      };
      return missingNameResult;
    }
    if (result && typeof result === 'object' && 'ok' in result) {
      return result as CreateDashboardResult;
    }
    return null;
  }, [createDashboardDraft]);

  /**
   * Handle error during dashboard operations.
   */
  const handleError = useCallback(
    (message: string) => {
      showErrorSnackbar(message);
    },
    [showErrorSnackbar]
  );

  /**
   * Handle success during dashboard operations.
   */
  const handleSuccess = useCallback(
    (message: string) => {
      showSuccessSnackbar(message);
    },
    [showSuccessSnackbar]
  );

  return {
    handleCreateDashboard,
    handleError,
    handleSuccess,
    showErrorSnackbar
  };
};
